/**
 * Where a charter is in its life. One way only — a reserve can be confirmed into a booking or called
 * off, and nothing comes back out of either.
 *
 * `Cancelled` is here for completeness rather than for the schedule: a cancelled charter is left out
 * of the window the schedule loads, so the only place one is read is the sidebar of somebody who had
 * it open, or opened it by address.
 *
 * The names are the server's enum names: `JsonStringEnumConverter` is registered globally, so this
 * crosses the wire as the word rather than a number the two ends would have to keep in step.
 */
export const CHARTER_STATUSES = ['Reserved', 'Booked', 'Cancelled'] as const;

export type CharterStatus = (typeof CHARTER_STATUSES)[number];

/**
 * The customer a charter is for.
 *
 * The name in its two halves with the email beside it, which is the shape the staff customer search
 * hands out — so the option picked in the form and the customer read back off a saved charter are
 * the same object, and `charterCustomerLabel` is the only thing that decides how one is written out.
 */
export interface CharterCustomer {
	id: string;
	firstName: string;
	lastName: string;
	email: string;

	/**
	 * The customer's type, which decides which of the dynamic fields restricted to customer types the
	 * charter is asked. Absent on a customer nobody has given one.
	 *
	 * Carried on the customer rather than looked up, so the fields are right on the first render of a
	 * charter opened from the schedule and the moment a customer is picked in the panel. Both the
	 * staff customer search and a charter read back off the server send it.
	 */
	userDisplayNameId?: string | null;

	/**
	 * The rest of the customer, which only a charter read back off the server carries — the picker
	 * narrows an option to the four fields above, so the copy held in the form has none of these.
	 * Read them off a fetched charter, never off form state.
	 */
	phone?: string;
	companyName?: string;
	isGuest?: boolean;
}

/**
 * The three things a booked charter's money can be.
 *
 * Fewer than the transaction has statuses: a reader needs to know whether an invoice is out, whether
 * money is owed without one, or whether it is settled — not which status produced that.
 */
export type CharterPaymentState = 'Awaiting' | 'Unpaid' | 'Paid';

/** The charter's transaction, as much of it as the card and the sidebar show. */
export interface CharterPayment {
	/** The transaction itself, which is what the payment form and every payment endpoint take. */
	transactionId: string;

	/** The invoice number staff quote, which is also the transaction's reference. */
	invoiceNumber?: string;

	state: CharterPaymentState;

	/** Set only once money changed hands — never for an invoice, which is a status change. */
	paidAt?: Date;

	/** How it was settled, and absent until it is. */
	paidMethod?: string;

	/**
	 * When a manager settled the charter by sending an invoice. Absent until one has been sent.
	 *
	 * The only thing that tells an invoice going out from a customer simply being the invoiced kind: a
	 * charter for one of those reads awaiting from the moment it is booked, so the state alone never
	 * says whether anybody acted.
	 */
	invoicedAt?: Date;
}

/** How a customer reads wherever one is shown: the picker's options, the card, the hover panel. */
export function charterCustomerLabel(customer: CharterCustomer): string {
	return `${customer.firstName} ${customer.lastName} (${customer.email})`;
}

/**
 * A charter, as much of one as the schedule needs to draw a card.
 *
 * Deliberately the smallest shape that will do. A charter has no route — the vessel is hired, and
 * where it goes is between the customer and the skipper — and its capacity is simply the vessel's,
 * so neither earns a line on a card in a narrow day column. What the card leaves out, the hover
 * detail picks up, and those fields are the optional block at the bottom.
 *
 * Matches the server's `CharterDto` field for field, and `CharterApi` is the only thing that turns
 * one into the other — the two date-times arrive as strings and are the sole conversion.
 */
export interface CharterScheduleItem {
	id: string;

	/**
	 * The charter's reference — the same kind of id a booking carries, and what a charter is
	 * identified by in the table, in search, and on a tax invoice.
	 *
	 * Optional only for a charter taken before charters had one; everything written since carries it.
	 * The name is not a substitute: it is optional and not unique.
	 */
	humanReadableId?: string;

	/**
	 * What the charter is called. Optional — a charter can be pencilled in before anyone has said
	 * what it is for, so every card has to read correctly without it.
	 */
	name?: string;

	/**
	 * Just enough of the vessel to name it and filter on it. Shaped like
	 * `TripSegmentDetailsDto.ferry` so the schedule's vessel filter reads a charter and a trip the
	 * same way.
	 */
	ferry: {
		id: string;
		name: string;
	};

	/**
	 * The block the vessel is held for. A charter isn't a departure at a point in time the way a
	 * scheduled trip is — it takes the vessel out of service between these two, which is what makes
	 * the end time worth showing on the card and what any overlap check will compare.
	 */
	startDateTime: Date;
	endDateTime: Date;

	/**
	 * Reserved or booked. Read by the card — a reserve is striped where a booking is solid — because
	 * the one thing a staff member has to be able to tell at a glance is which of the two a block of
	 * held ferry time is. Never `Cancelled` on a card: the schedule doesn't send those.
	 */
	status: CharterStatus;

	// ── Hover detail ────────────────────────────────────────────
	// Never shown on the card itself, only in the pop-out. All optional: a charter gets pencilled
	// into the schedule long before most of this is agreed, so the detail panel has to read as an
	// incomplete booking rather than a broken one, and says "Not set" where a field is missing.

	/**
	 * Who the charter is for. Absent while it's an unassigned hold on the vessel — a reserve is often
	 * taken before anyone has said whose it is. Never absent on a booked charter.
	 */
	customer?: CharterCustomer;

	/** The negotiated hire price for the whole vessel, not a per-passenger fare. */
	price?: number;

	/**
	 * What the operator kept when the charter was cancelled. Absent on every charter nobody has
	 * cancelled, and `0` on one cancelled without keeping anything — so the two are told apart by
	 * whether it is there, never by whether it is truthy.
	 */
	cancellationFee?: number;

	/**
	 * Where the charter's money is up to. Absent on a reserved charter, which owes nothing.
	 */
	payment?: CharterPayment;

	/**
	 * Whether the charter has been handed to accounting — on an invoice report export, or by a
	 * manager marking it. Not the same as the customer having been sent an invoice, and not a
	 * payment: a marked charter still reads **Awaiting** until the money arrives.
	 *
	 * Absent on anything with no invoice to raise: a reserve, and a charter nobody is invoicing.
	 */
	isInvoiced?: boolean;

	/** Free text from whoever took the booking. */
	notes?: string;

	/**
	 * Answers to the dynamic fields aimed at charters, as pairs of field id and answer.
	 *
	 * Ids rather than labels: the client already holds every field definition, so a label copied at
	 * save time would read differently from the same field on a booking the moment an admin renamed
	 * it. Absent on a charter saved before charters had fields.
	 */
	dynamicFields?: Array<{ fieldId: string; value: string }>;
}
